import React, { useEffect, useRef, useState } from 'react';

// The widest the flyout gets (.col-menu's max-width in bravo-app.css), and the gap it keeps from the viewport's edge.
const MENU_MAX_WIDTH = 320;
const VIEWPORT_GAP = 8;

// The toolbar's Columns button and its flyout: a checkbox per column (a locked one cannot be unticked) and a Reset
// pill. The list toolbar (GeneralSearchStrip) shows it for every grid, and a page with a toolbar of its own can place
// it there. `columns` is [{ key, label, locked }] (see columnVisibilityHelper.getSelectableColumns) and
// `hiddenColumns` a Set of the hidden keys. Nothing is rendered when no column can be hidden.
export default function ColumnsMenu({
  columns = [],
  hiddenColumns = null,
  onToggleColumn = (key) => {},
  onResetColumns = () => {},
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  // Close the flyout on Escape or a click outside it and its button.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDown = (e) => {
      const menu = menuRef.current;
      const button = buttonRef.current;
      if (menu && menu.contains(e.target)) return;
      if (button && button.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  if (!columns || !columns.some((c) => !c.locked)) return null;
  const hidden = hiddenColumns || new Set();
  const hasHidden = columns.some((c) => !c.locked && hidden.has(c.key));

  // The flyout is a fixed DS .menu under the button. It grows leftward from the button's right edge, as in the list
  // toolbar where the button sits on the right, and rightward from its left edge when there is no room for that.
  const renderMenu = () => {
    const rect = buttonRef.current ? buttonRef.current.getBoundingClientRect() : null;
    const roomOnTheLeft = rect && rect.right >= MENU_MAX_WIDTH + VIEWPORT_GAP;
    const style = !rect
      ? undefined
      : roomOnTheLeft
      ? { top: rect.bottom + 6, right: Math.max(VIEWPORT_GAP, window.innerWidth - rect.right) }
      : { top: rect.bottom + 6, left: Math.max(VIEWPORT_GAP, rect.left) };
    return (
      <div
        className='menu col-menu'
        role='dialog'
        ref={menuRef}
        style={style}
      >
        <div className='col-menu-head'>
          <span className='col-menu-title'>Columns</span>
          <button
            type='button'
            className='col-menu-reset'
            onClick={() => onResetColumns()}
          >
            <i className='fa-solid fa-arrow-rotate-left' />
            <span>Reset</span>
          </button>
        </div>
        <div className='col-list'>
          {columns.map((c) => (
            <label
              key={c.key}
              className={`col-row ${c.locked ? 'locked' : ''}`}
            >
              <span className='clbl'>{c.label}</span>
              <input
                className='cbx'
                type='checkbox'
                checked={!hidden.has(c.key)}
                disabled={c.locked}
                onChange={() => onToggleColumn(c.key)}
              />
            </label>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <button
        type='button'
        ref={buttonRef}
        className='icon-btn col-toggle-btn'
        onClick={() => setOpen((v) => !v)}
        title={hasHidden ? 'Columns (some hidden)' : 'Columns'}
      >
        <i className='fa-solid fa-table-columns' />
        {hasHidden && <span className='col-dot' />}
      </button>
      {open && renderMenu()}
    </>
  );
}
